import moment from 'moment';
import React, { useContext, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import Dialog from 'src/components/common/Dialog.redesign';
import Button from 'src/components/controls/forms/redesign/FormButton';
import RHFCheckbox from 'src/components/controls/tempRHFControls/RHFCheckBox';
import RHFInputBox from 'src/components/controls/tempRHFControls/RHFInputBox';
import OwnershipLeaseBuyer, { LeaseBuyerContent, OwnershipLeaseBuyerForm } from 'src/models/OwnershipLeaseBuyer';
import { AppContext } from 'src/providers/AppContextProvider';
import { IAppContextState } from 'src/reducers/AppContext';
import AgreementUtil from 'src/util/AgreementUtil';
import useFetch from 'use-http';

interface IEmailLeaseBuyersDialogProps {
    leaseBuyerList: OwnershipLeaseBuyer[];

    onSave: () => void;
    onCloseDialog?: () => void;
}

const EmailLeaseBuyersDialog = (props: IEmailLeaseBuyersDialogProps) => {
    const { reset, register, errors, handleSubmit, setValue } = useForm();
    const { response, post } = useFetch();
    const appContext = useContext<IAppContextState>(AppContext);

    useEffect(() => {
        if (props.leaseBuyerList.length > 0) {
            reset({ leaseBuyers: props.leaseBuyerList });
        }
    }, [props]);

    const displayLeaseBuyerForm = (oIndex: number) => {
        return (
            <div style={{ display: 'flex', justifyContent: 'center' }} key={`leaseBuyer-${oIndex}`}>
                <RHFCheckbox
                    name={`leaseBuyers[${oIndex}].sendEmail`}
                    register={register}
                    errors={errors}
                    bodyStyle={{ width: '3%' }}
                    style={{ width: '100%' }}
                />
                <RHFInputBox
                    name={`leaseBuyers[${oIndex}].leaseBuyerEmail`}
                    register={register}
                    errors={errors}
                    bodyStyle={{ width: '10%' }}
                    style={{ width: '100%' }}
                    disabled={true}
                />
                <RHFInputBox
                    name={`leaseBuyers[${oIndex}].ccEmail`}
                    register={register}
                    errors={errors}
                    bodyStyle={{ width: '10%' }}
                    style={{ width: '100%' }}
                />
                <RHFInputBox
                    name={`leaseBuyers[${oIndex}].lessor`}
                    register={register}
                    errors={errors}
                    bodyStyle={{ width: '10%' }}
                    style={{ width: '100%' }}
                />
                <RHFInputBox
                    name={`leaseBuyers[${oIndex}].globalId`}
                    register={register}
                    errors={errors}
                    bodyStyle={{ width: '8%' }}
                    style={{ width: '100%' }}
                />
                <RHFInputBox
                    name={`leaseBuyers[${oIndex}].tractDescription`}
                    register={register}
                    errors={errors}
                    bodyStyle={{ width: '10%' }}
                    style={{ width: '100%' }}
                />
                <RHFInputBox
                    name={`leaseBuyers[${oIndex}].grossAcres`}
                    register={register}
                    type="number"
                    errors={errors}
                    bodyStyle={{ width: '5%' }}
                    style={{ width: '100%' }}
                    disabled={true}
                />
                <RHFInputBox
                    name={`leaseBuyers[${oIndex}].netAcres`}
                    register={register}
                    type="number"
                    errors={errors}
                    bodyStyle={{ width: '5%' }}
                    style={{ width: '100%' }}
                    disabled={true}
                />

                <RHFInputBox
                    name={`leaseBuyers[${oIndex}].county`}
                    register={register}
                    errors={errors}
                    bodyStyle={{ width: '8%' }}
                    style={{ width: '100%' }}
                    disabled={true}
                />
                <RHFInputBox
                    name={`leaseBuyers[${oIndex}].status`}
                    register={register}
                    errors={errors}
                    bodyStyle={{ width: '8%' }}
                    style={{ width: '100%' }}
                    disabled={true}
                />
                <RHFInputBox
                    name={`leaseBuyers[${oIndex}].addlOwners`}
                    register={register}
                    type="number"
                    errors={errors}
                    bodyStyle={{ width: '5%' }}
                    style={{ width: '100%' }}
                    disabled={true}
                />
                <RHFInputBox
                    name={`leaseBuyers[${oIndex}].mineralCategory`}
                    register={register}
                    errors={errors}
                    bodyStyle={{ width: '10%' }}
                    style={{ width: '100%' }}
                />
                <RHFInputBox
                    name={`leaseBuyers[${oIndex}].assignment`}
                    register={register}
                    errors={errors}
                    bodyStyle={{ width: '8%' }}
                    style={{ width: '100%' }}
                />
            </div>
        );
    };

    const headerList = () => {
        return (
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <div className="form-field" style={{ width: '3%' }}>Send?</div>
                <div className="form-field" style={{ width: '10%' }}>Lease Buyer</div>
                <div className="form-field" style={{ width: '10%' }}>CC</div>
                <div className="form-field" style={{ width: '10%' }}>Lessor</div>
                <div className="form-field" style={{ width: '8%' }}>Global Id</div>
                <div className="form-field" style={{ width: '10%' }}>Tract Description</div>
                <div className="form-field" style={{ width: '5%' }}>Gross Acres</div>
                <div className="form-field" style={{ width: '5%' }}>Net Acres</div>
                <div className="form-field" style={{ width: '8%' }}>County</div>
                <div className="form-field" style={{ width: '8%' }}>Status</div>
                <div className="form-field" style={{ width: '5%' }}>Addl Owners</div>
                <div className="form-field" style={{ width: '10%' }}>Mineral Category</div>
                <div className="form-field" style={{ width: '8%' }}>Assignment</div>
            </div>
        );
    };

    const rejectEmails = () => {
        props.onSave();
        props.onCloseDialog();
    };

    const selectAll = () => {
        for (let i = 0; i < props.leaseBuyerList.length; i++) {
            setValue(`leaseBuyers[${i}].sendEmail`, true);
        }
    };

    const onSubmit = async (data: { leaseBuyers: OwnershipLeaseBuyerForm[] }) => {
        let cleaned = finalizeData(data.leaseBuyers);

        let mergeUrl = `/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/owners/email-lease-buyers`;

        await post(mergeUrl, cleaned);

        if (response.ok) {
            props.onSave();
            props.onCloseDialog();
        }
    };

    const finalizeData = (leaseBuyers: OwnershipLeaseBuyerForm[]): OwnershipLeaseBuyer[] => {
        let emailList: OwnershipLeaseBuyer[] = [];

        for (let lb of leaseBuyers) {
            if (AgreementUtil.parseBool(lb.sendEmail + '') === true) {
                // Create row.
                let content: LeaseBuyerContent = {
                    lessor: lb.lessor,
                    globalId: lb.globalId,
                    tractDescription: lb.tractDescription,
                    county: lb.county,
                    grossAcres: lb.grossAcres ? parseInt(lb.grossAcres + '', 10) : 0,
                    netAcres: lb.netAcres ? parseInt(lb.netAcres + '', 10) : 0,
                    addlOwners: lb.addlOwners ? parseInt(lb.addlOwners + '', 10) : 0,
                    status: lb.status,
                    mineralCategory: lb.mineralCategory,
                    assignment: lb.assignment,
                    assignmentDate: moment(new Date()).format('MM/DD/YYYY')
                };
    
                // Find existing email.
                let index = emailList.find(el => el.leaseBuyerEmail === lb.leaseBuyerEmail);
                if (index !== undefined) {
                    // Add row
                    index.leaseBuyerContent.push(content);
                } else {
                    // Create email and add row.
                    let newBuyer: OwnershipLeaseBuyer = {
                        leaseBuyerEmail: lb.leaseBuyerEmail,
                        ccEmail: lb.ccEmail,
                        leaseBuyerContent: [content]
                    };
    
                    emailList.push(newBuyer);
                }
            }
        }

        return emailList;
    };

    return (
        <Dialog title="Notify Lease Buyers">
            <div style={{ width: '90vw' }}>
                <form onSubmit={handleSubmit(onSubmit)} id="contractForm">

                    {headerList()}
                    {props.leaseBuyerList.map((_, index: number) => {
                        return displayLeaseBuyerForm(index);
                    })}
                    <div className="mt-2">
                        <Button className="mr-1" text="Notify" type="submit" intent="danger" />
                        <Button className="mr-1" text="Select All" onClick={() => selectAll()} intent="primary" />
                        <Button text="Ignore All" onClick={() => rejectEmails()} intent="primary" />
                    </div>
                </form>
            </div>
        </Dialog>
    );
};

export default EmailLeaseBuyersDialog;


// WEBPACK FOOTER //
// ./src/components/project/sections/LeasingComponents/EmailLeaseBuyersDialog.tsx